import { memo, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import { ContactManagerSidebar } from '@modules/ContactManager/components/Sidebar';
import { IAssignedUser, IContactAssignedUser } from '@modules/admin/AdminTools/Roles/RoleModal/models/RoleAssignmentModel';
import { ILead } from '@modules/ContactManager/components/Tabs/components/Leads/types/interfaces/interfaces';
import { SERVICES } from '@shared/constants/apiConfig';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { IOption } from '@shared/interfaces';
import { useRefreshComments } from '@shared/hooks/useRefreshComments';
import { setLastCommunicationDate } from '@store/reducers/contactManagerSlice';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Roles } from '@shared/constants/constants';
import { IDropdownUsersProps } from '@modules/ContactManager/interfaces';
import { useLoadCurrentWorkFlowLead } from '@modules/ContactManager/components/Header/requests';
import { ContactUserTypeEnum, ContactUserTypeLabel, orderedContactDropdown } from '../../components/Deals/interfaces';
import { isSpecificDropdownUsersProps } from '../../components/Deals/components/DealUsers';
import { ContactLeadsModal } from './ContactLeadsModal/ContactLeadsModal';
import { requestChangeAssignedUser, requestUnAssignUser } from './requests';

type ContactUserBlockPropsType = {
  contactId: string;
  leadId: string;
  userSelectOptions: IDropdownUsersProps | IOption<unknown>[];
  userSelectAllOptions: IDropdownUsersProps | IOption<unknown>[];
};

type InitStateType = {
  label: string;
  userType: ContactUserTypeEnum;
  userId: string | null;
  options: IOption[];
  defaultValue?: IOption;
};

type modalData = {
  state: number;
  userType: ContactUserTypeEnum;
}

export const getContactOptions = (
  contactUserType: ContactUserTypeEnum,
  userSelectOptions: IDropdownUsersProps | IOption[],
  selectedUserId: string | undefined,
  userSelectAllOptions: IDropdownUsersProps | IOption[]
): IOption[] => {
  let options: IOption[] = [];

  if (isSpecificDropdownUsersProps(userSelectOptions)) {
    switch (contactUserType) {
      case ContactUserTypeEnum.SDR:
        options = userSelectOptions.sdrUserSelectOptions;
        break;
      default:
        options = userSelectOptions.lcUserSelectOptions;
    }
  } else {
    options = userSelectOptions;
  }

  const userExists = options.some((opt) => opt.value === selectedUserId);

  if (!userExists && selectedUserId) {
    if (!isSpecificDropdownUsersProps(userSelectAllOptions)) {
      const fallbackUser = userSelectAllOptions.find((opt) => opt.value === selectedUserId);
      if (fallbackUser) {
        options = [...options, fallbackUser];
      }
    }
  }

  return options;
};

export const ContactUserBlock = memo(({ contactId, leadId, userSelectOptions, userSelectAllOptions }: ContactUserBlockPropsType) => {
  const { data: assignedUser, mutate } = useSWR<IAssignedUser[]>(contactId && `/${SERVICES.CONTACT}/assignUser/list/${contactId}`);
  const { data: leads = [], mutate: mutateLeads } = useSWR<ILead[]>(`/${SERVICES.LEAD}/contacts/${contactId}/leadswithoverride`, {
    revalidateOnMount: false
  });

  const initialModalState = { state: 0 } as modalData;
  const mutateComments = useRefreshComments(false, contactId);
  const {
    reloadCurrentWorkFlowLead
  } = useLoadCurrentWorkFlowLead(leadId);
  const { isAdmin, userHasRoles } = useUserAccount();

  const disabled = !(isAdmin || userHasRoles([Roles.LC_LEAD]));

  const [selectedValues, setSelectedValues] = useState<Record<ContactUserTypeEnum, IContactAssignedUser> | Record<string, never>>({});

  const [modalOpenStatus, handleModalOpenStatus] = useState<modalData>(initialModalState);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  const data: InitStateType[] = useMemo(
    () => {
      const mappedSelectedUserIds: Record<ContactUserTypeEnum, IContactAssignedUser> | Record<string, never> = (assignedUser ?? []).reduce((acc, cur) => {
        if (cur.userType === ContactUserTypeEnum.SDR || cur.userType === ContactUserTypeEnum.Lc) {
          return {
            ...acc,
            [cur.userType]: cur,
          };
        }
        return acc;
      }, {});
      setSelectedValues(mappedSelectedUserIds);
      const mappedDropdownData = orderedContactDropdown.map((userType) => {
        return {
          userType,
          label: ContactUserTypeLabel[userType],
          userId: (mappedSelectedUserIds[userType] as IContactAssignedUser)?.userId,
          options: getContactOptions(
            userType,
            userSelectOptions,
            (mappedSelectedUserIds[userType] as IContactAssignedUser)?.userId,
            userSelectAllOptions
          ),
        };
      });

      return mappedDropdownData;
    },
    [userSelectOptions, assignedUser]);

  const handleSubmitTransfer = useCallback(
    (leadIds: string[]) => {
      setIsLoading(true);
      requestChangeAssignedUser(selectedValues && (selectedValues[modalOpenStatus.userType]?.userId || '') || '', {
        leadIds: leadIds,
        contactId: contactId,
        userType: modalOpenStatus.userType,
      }).then(() => {
        handleModalOpenStatus(initialModalState);
        mutate();
        mutateLeads();
        mutateComments();
        reloadCurrentWorkFlowLead();
        dispatch(setLastCommunicationDate(new Date()));
        setIsLoading(false);
      });
    },
    [requestChangeAssignedUser, mutate, modalOpenStatus, selectedValues]
  );

  const handleUnAssignUser = useCallback(
    (contactId: string, userType: ContactUserTypeEnum, userName: string | undefined) => {
      setIsLoading(true);
      requestUnAssignUser(contactId, userType, userName)
      .then(() => {
        mutate();
        mutateComments();
        dispatch(setLastCommunicationDate(new Date()));
        setIsLoading(false);
      });
    },
    [mutateComments, mutate, selectedValues]
  );

  const handleChangeAssignedUser = useCallback(
    (userId: string, userType: ContactUserTypeEnum) => {
      setIsLoading(true);
      requestChangeAssignedUser(userId, {
        leadIds: [],
        contactId: contactId,
        userType: userType,
      },
      ).then(() => {
        mutate();
        mutateComments();
        dispatch(setLastCommunicationDate(new Date()));
        setIsLoading(false);
      });
    },
    [mutateComments, mutate, selectedValues]
  );

  const onChange = useCallback(
    (key: ContactUserTypeEnum, selectedUser: IOption, options: IOption[]) => {
      setSelectedValues(prevValues => {
        if (!selectedUser) {
          const userName = options.find((option) => option.value === selectedValues[key]?.userId)?.label;
          handleUnAssignUser(contactId, key, userName);
          delete prevValues[key];
          return {
            ...prevValues,
          };
        }
        if (key === ContactUserTypeEnum.Lc) {
          handleModalOpenStatus((prevState) => ({
            state: prevState.state + 1,
            userType: key,
          }));
          mutateLeads();
        } else handleChangeAssignedUser(selectedUser.value as string, key);
        return {
          ...prevValues,
          [key]: {
            userType: key,
            userId: selectedUser.value,
            userName: selectedUser.label
          }
        };
      });
    },
    [handleUnAssignUser, handleChangeAssignedUser, handleModalOpenStatus]
  );

  return (
    <>
      <ContactManagerSidebar
        title="Contact Users"
        contactId={contactId}
      >
        <>
          {data?.map((item) => (
            item.options.length > 0 && (item.userType === ContactUserTypeEnum.SDR || item.userType === ContactUserTypeEnum.Lc) &&
            <Grid
              key={item.userType}
              container
              alignItems="center"
            >
              <Grid item xs={4}>
                {item.label}
              </Grid>

              <Grid item xs>
                <SelectAutocomplete
                  fullWidth
                  removeExtraPadding
                  canBeClean={item.userType === ContactUserTypeEnum.SDR}
                  disabled={disabled}
                  openOnFocus={false}
                  value={selectedValues[item.userType]?.userId}
                  placeholder="Select User"
                  options={item.options}
                  onChange={(_value, user: IOption<string>) => onChange(item.userType, user, item.options)}
                />
              </Grid>
            </Grid>
          ))}
        </>
      </ContactManagerSidebar>

      {!!modalOpenStatus.state && (
        <ContactLeadsModal
          ContactLeadsModalPosition={modalOpenStatus.state}
          handleClose={() => handleModalOpenStatus(initialModalState)}
          handleSubmit={handleSubmitTransfer}
          leads={leads}
          isLoading={isLoading}
        />
      )}
    </>
  );
});
